import {
  Table,
  type Column,
  type Row,
  type SortDescriptor,
  LinkNameCell,
} from '@ardoq/table';
import { autoSizeColumnProps } from './columnProps';
import { GremlinComponentResult, GremlinResultType } from '../types';
import { formatValue } from '../utils';
import { toLinkProps } from 'router/declarative/toLinkProps';
import { dispatchAction } from '@ardoq/rxbeach';
import { showGremlinResultDetails } from '../../actions';
import { SortByFn, SortById } from '@ardoq/pagination';
import { getIn } from 'utils/collectionUtil';
import { SortOrder } from '@ardoq/api-types';
import type { FieldLabelByName } from 'models/utils/fieldOps';
import { getGremlinTableSizeProps } from './tableSizeProps';
import { toUniqueRowIds } from '../rowIds';
import { ButtonSize, TertiaryButton } from '@ardoq/button';
import { FlexBox } from '@ardoq/layout';
import { t, type I18nLocale, type WithI18nLocale } from '@ardoq/i18n';
import { type Locale } from '@ardoq/locale';

const ID_COLUMN_ID = 'id';
const NAME_COLUMN_ID = 'name';
const ACTIONS_COLUMN_ID = '__actions';

type GremlinResultsTableComponentProps = {
  results: GremlinComponentResult[];
  printable?: boolean;
  sortBy: SortByFn;
  sortById: SortById;
  sortOrder?: SortOrder;
  selectedColumns: string[];
  fieldLabelByName: FieldLabelByName;
  locale: Locale;
  i18nLocale: I18nLocale;
};

type CellData = { value: string; result: GremlinComponentResult };
type RowData = Record<string, CellData>;

const getComponentName = (result: GremlinComponentResult): string =>
  getIn<string>(result, ['properties', 'name', 0, 'value']) || '';

const getPropertyValue = (
  result: GremlinComponentResult,
  columnName: string,
  locale: Locale
): string => {
  const value = getIn<string | boolean>(result, [
    'properties',
    columnName,
    0,
    'value',
  ]);
  if (value === undefined || value === null) return '';
  return String(formatValue(value, locale));
};

const IdCell = ({ value }: CellData) => <>{value}</>;

const NameCell = ({ value, result }: CellData) => {
  const { href, onClick } = toLinkProps({
    route: '/component-overview/:componentId',
    params: { componentId: result.id },
  });
  return (
    <LinkNameCell displayName={value} href={href} onClick={() => onClick?.()} />
  );
};

const ValueCell = ({ value }: CellData) => <>{value}</>;

const getValue = ({ value }: CellData): string => value;

type ShowDetailsCellProps = CellData & WithI18nLocale;

const ShowDetailsCell = ({ result, i18nLocale }: ShowDetailsCellProps) => (
  <FlexBox justify="center" height="full" align="center">
    <TertiaryButton
      buttonSize={ButtonSize.SMALL}
      onClick={() =>
        dispatchAction(
          showGremlinResultDetails({
            result,
            resultType: GremlinResultType.COMPONENT,
          })
        )
      }
    >
      {t(i18nLocale, 'search:gremlinResultsTable.showDetails', 'Show details')}
    </TertiaryButton>
  </FlexBox>
);

const buildColumns = (
  selectedColumns: string[],
  fieldLabelByName: FieldLabelByName,
  printable: boolean,
  i18nLocale: I18nLocale
): Column<RowData>[] => {
  const columns: Column<RowData>[] = [
    {
      id: ID_COLUMN_ID,
      title: t(i18nLocale, 'search:gremlinResultsTable.id', 'Id'),
      cellRenderer: IdCell,
      valueGetter: getValue,
      width: 240,
      ...autoSizeColumnProps,
    },
    {
      id: NAME_COLUMN_ID,
      title: t(i18nLocale, 'search:gremlinResultsTable.name', 'Name'),
      cellRenderer: NameCell,
      valueGetter: getValue,
      ...autoSizeColumnProps,
    },
    ...selectedColumns.map<Column<RowData>>(columnName => ({
      id: columnName,
      title: fieldLabelByName[columnName] || columnName,
      cellRenderer: ValueCell,
      valueGetter: getValue,
      minWidth: 100,
      ...autoSizeColumnProps,
    })),
  ];
  if (!printable) {
    columns.push({
      id: ACTIONS_COLUMN_ID,
      title: '',
      cellRenderer: (cellData: CellData) => (
        <ShowDetailsCell {...cellData} i18nLocale={i18nLocale} />
      ),
      valueGetter: () => '',
      width: 150,
      suppressSorting: true,
      suppressHeaderContextMenu: true,
      resizable: false,
    });
  }
  return columns;
};

const toRows = (
  results: GremlinComponentResult[],
  selectedColumns: string[],
  locale: Locale
): Row<RowData>[] => {
  const rowIds = toUniqueRowIds(results.map(result => result.id));
  return results.map((result, index) => {
    const data: RowData = {
      [ID_COLUMN_ID]: { value: result.id, result },
      [NAME_COLUMN_ID]: { value: getComponentName(result), result },
      [ACTIONS_COLUMN_ID]: { value: '', result },
    };
    selectedColumns.forEach(columnName => {
      data[columnName] = {
        value: getPropertyValue(result, columnName, locale),
        result,
      };
    });
    return { id: rowIds[index], data };
  });
};

const toSortDescriptor = (
  sortById: SortById,
  sortOrder: SortOrder | undefined
): SortDescriptor | null => {
  if (!sortById || !sortOrder) return null;
  return {
    column: String(sortById),
    direction: sortOrder === SortOrder.ASC ? 'asc' : 'desc',
  };
};

const GremlinComponentsResultTable = ({
  results,
  printable = false,
  sortBy,
  sortById,
  sortOrder,
  selectedColumns,
  fieldLabelByName,
  locale,
  i18nLocale,
}: GremlinResultsTableComponentProps) => (
  <Table<RowData>
    aria-label={t(
      i18nLocale,
      'search:gremlinResultsTable.componentAriaLabel',
      'Gremlin component results'
    )}
    rows={toRows(results, selectedColumns, locale)}
    columns={buildColumns(
      selectedColumns,
      fieldLabelByName,
      printable,
      i18nLocale
    )}
    sortable={{
      descriptor: toSortDescriptor(sortById, sortOrder),
      externallySorted: true,
      onChange: descriptor => {
        if (!descriptor) return;
        const { column } = descriptor;
        if (column === NAME_COLUMN_ID) {
          sortBy(getComponentName, NAME_COLUMN_ID);
        } else {
          sortBy(column);
        }
      },
    }}
    {...getGremlinTableSizeProps(results.length)}
  />
);

export { GremlinComponentsResultTable };
